import { useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type ChatChannel = 'team' | 'help';
export type SenderType = 'team_member' | 'facilitator' | 'system';

export interface ChatMessage {
  id: string;
  eventId: string;
  teamId: string;
  senderId: string;
  senderName: string;
  senderType: SenderType;
  channel: ChatChannel;
  content: string;
  createdAt: string;
  updatedAt: string;
}

interface MessagesResponse {
  success: boolean;
  data: ChatMessage[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface UseTeamMessagesOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

/**
 * Hook to fetch messages for a team's channel
 */
export function useTeamMessages(
  eventId: string | undefined,
  teamId: string | undefined,
  channel: ChatChannel = 'help',
  options: UseTeamMessagesOptions = {},
) {
  const apiClient = useApiClient();
  const { enabled = true, refetchInterval = 5000 } = options;

  return useQuery<MessagesResponse>({
    queryKey: ['TeamMessages', eventId, teamId, channel],
    queryFn: () =>
      apiClient.get(`/events/${eventId}/teams/${teamId}/messages`, {
        params: { channel, limit: 100 },
      }),
    enabled: enabled && !!eventId && !!teamId,
    refetchInterval,
    staleTime: 2000,
  });
}

interface SendMessageInput {
  content: string;
  channel?: ChatChannel;
}

/**
 * Hook to send a message as a facilitator
 */
export function useSendFacilitatorMessage(eventId: string | undefined, teamId: string | undefined) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SendMessageInput) => {
      return apiClient.post(`/events/${eventId}/teams/${teamId}/messages`, {
        content: input.content,
        channel: input.channel || 'help',
      });
    },
    onSuccess: () => {
      // Invalidate the messages query to refetch
      queryClient.invalidateQueries({ queryKey: ['TeamMessages', eventId, teamId] });
      // Also invalidate the needs-response query since we just responded
      queryClient.invalidateQueries({ queryKey: ['TeamsNeedingResponse', eventId] });
    },
  });
}

export interface TeamMessageStatus {
  teamId: string;
  lastMessageAt: string;
  needsResponse: boolean;
}

interface TeamsMessageStatusResponse {
  success: boolean;
  data: TeamMessageStatus[];
}

/**
 * Hook to fetch teams' help channel activity and response status
 */
export function useTeamsMessageStatus(
  eventId: string | undefined,
  options: { enabled?: boolean; refetchInterval?: number } = {},
) {
  const apiClient = useApiClient();
  const { enabled = true, refetchInterval = 5000 } = options;

  return useQuery<TeamsMessageStatusResponse>({
    queryKey: ['TeamsNeedingResponse', eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/messages/needs-response`),
    enabled: enabled && !!eventId,
    refetchInterval,
    staleTime: 2000,
  });
}
